/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { useEffect, useRef, useState } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, Chip, Stack, Typography } from '@mui/material';
import { RefreshOutlined, ExpandMore, ExpandLess, Cancel } from '@mui/icons-material';
import { TableTooltip } from '../StyledComponents/TableTooltip';
import clsx from 'clsx';

export const SELECTED_FILTERS_PANEL_MIN_HEIGHT = 41;
export const SELECTED_FILTERS_PANEL_MAX_HEIGHT = 168;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'row',
            padding: theme.spacing(0.5, 1, 0.5, 2.5),
            backgroundColor: theme.palette.lightTeal.main,
            borderTop: '1px solid',
            overflow: 'auto',
            width: '100%',
            maxHeight: SELECTED_FILTERS_PANEL_MAX_HEIGHT,
        },
        rootNarrow: {
            height: SELECTED_FILTERS_PANEL_MIN_HEIGHT,
        },
        rootFitContent: {
            height: 'fit-content',
            borderBottom: '1px solid',
        },
        chipsWrapper: {
            flexGrow: 1,
        },
        chip: {
            margin: theme.spacing(0.5),
            backgroundColor: 'white',
            maxWidth: 300,
        },
        chipFitContent: {
            maxWidth: 'fit-content',
        },
        placeholderText: {
            fontSize: 14,
            opacity: 0.5,
            alignSelf: 'center',
            flexGrow: 1,
        },
        positionedBar: {
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
        },
        buttonsGroup: {
            display: 'flex',
            alignItems: 'flex-start',
            margin: theme.spacing(0, 2, 0, 1.5),
        },
        button: {
            backgroundColor: 'white',
            '&:disabled': {
                color: theme.palette.disabledText.main,
                backgroundColor: 'white',
                borderColor: theme.palette.disabledText.main,
            },
            '&:hover': {
                backgroundColor: theme.palette.scrollBar.light,
            },
        },
        leftButton: {
            borderTopRightRadius: '0',
            borderBottomRightRadius: '0',
            borderRightColor: 'transparent',
        },
        rightButton: {
            borderTopLeftRadius: '0',
            borderBottomLeftRadius: '0',
            marginLeft: '-1px',
        },
    }),
);

export interface FilterChipProps<T extends object> {
    keyForValue: keyof T;
    formattedValue: string;
    index?: number;
    pairedKeyForValue?: keyof T;
    resetValue?: unknown;
    nonResettable?: boolean;
    fitContent?: boolean;
}

interface SelectedFilterPanelProps<T extends object> {
    onDeleteFilter: (props: FilterChipProps<T>) => void;
    onApplyFilters: () => void;
    filters: FilterChipProps<T>[];
    disableApplyButton: boolean;
}

export const SelectedFilterPanel = <T extends object>(props: SelectedFilterPanelProps<T>) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters, disableApplyButton, onDeleteFilter, onApplyFilters } = props;

    const [isExpanded, setIsExpanded] = useState(false);
    const [isMoreThanOneLine, setIsMoreThanOneLine] = useState(false);
    const chipsWrapperRef = useRef(null);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            if (chipsWrapperRef.current)
                setIsMoreThanOneLine(chipsWrapperRef.current.scrollHeight > SELECTED_FILTERS_PANEL_MIN_HEIGHT);
        });

        if (chipsWrapperRef.current) {
            resizeObserver.observe(chipsWrapperRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (chipsWrapperRef.current) {
            if (isMoreThanOneLine !== chipsWrapperRef.current.scrollHeight > SELECTED_FILTERS_PANEL_MIN_HEIGHT)
                setIsMoreThanOneLine(chipsWrapperRef.current.scrollHeight > SELECTED_FILTERS_PANEL_MIN_HEIGHT);
        }
    });

    return (
        <div>
            <div className={classes.rootNarrow} />
            <div
                data-testid="ui-filter-selected-panel"
                ref={chipsWrapperRef}
                className={clsx(
                    classes.root,
                    isExpanded ? classes.rootFitContent : classes.rootNarrow,
                    classes.positionedBar,
                )}
            >
                {filters && filters.length > 0 ? (
                    <Stack className={classes.chipsWrapper} direction="row" spacing={0} flexWrap="wrap">
                        {filters.map((filter, index) => {
                            return (
                                <Chip
                                    data-testid="ui-filter-selected-chip"
                                    key={index}
                                    className={clsx(classes.chip, filter.fitContent ? classes.chipFitContent : null)}
                                    label={filter.formattedValue}
                                    variant="outlined"
                                    size="small"
                                    onDelete={filter.nonResettable ? null : () => onDeleteFilter(filter)}
                                    deleteIcon={<Cancel role="img" aria-hidden={false} aria-label={t('app.delete')} />}
                                />
                            );
                        })}
                    </Stack>
                ) : (
                    <Typography className={classes.placeholderText} noWrap>
                        {t('app.selectedFilters')}
                    </Typography>
                )}
                <ButtonGroup className={classes.buttonsGroup}>
                    <TableTooltip placement="top" title={t('domain.update')}>
                        <span>
                            <Button
                                data-testid="ui-filter-apply-button"
                                className={clsx(classes.button, classes.leftButton)}
                                onClick={onApplyFilters}
                                disabled={disableApplyButton}
                                size="small"
                            >
                                <RefreshOutlined />
                            </Button>
                        </span>
                    </TableTooltip>
                    <TableTooltip placement="top" title={isExpanded ? t('app.collapseAll') : t('app.unhide')}>
                        <span>
                            <Button
                                className={clsx(classes.button, classes.rightButton)}
                                onClick={() => setIsExpanded(!isExpanded)}
                                size="small"
                                disabled={!isMoreThanOneLine}
                            >
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </Button>
                        </span>
                    </TableTooltip>
                </ButtonGroup>
            </div>
        </div>
    );
};
